/**
 * Token utilities for auth features
 * Provides client-side helpers to decode and inspect token payloads when possible.
 * Note: Encrypted tokens (server-only) cannot be decoded client-side; callers should
 * fall back to server-provided metadata where available.
 */

import { User } from '@/domains/auth/services/auth-service';
import { base64UrlToBase64, decodeBase64UrlJson } from '@/shared/utils/base64';

export interface DecodedToken {
  sub: string;
  email?: string;
  username?: string;
  iat?: number;
  exp?: number;
  type?: string;
  [key: string]: any;
}

export interface TokenValidationResult {
  isValid: boolean;
  isExpired: boolean;
  isExpiringSoon: boolean;
  secondsUntilExpiration: number;
  payload?: DecodedToken;
  error?: string;
}

export const TOKEN_KEYS = {
  ACCESS_TOKEN: 'session_access_token',
  PROVISIONAL_TOKEN: 'session_provisional_token',
  REFRESH_TOKEN: 'session_refresh_token',
  USER: 'session_user',
  TOKEN_EXPIRY: 'session_token_expiry',
  MFA_USER_ID: 'session_mfa_user_id',
} as const;

export const USER_STORAGE_KEY = TOKEN_KEYS.USER;
export const DEFAULT_EXPIRATION_BUFFER_MS = 5 * 60 * 1000;

/**
 * Decode token payload without verification. Returns parsed payload for public/plain tokens.
 * If token is encrypted or cannot be parsed, null is returned and callers may query
 * stored metadata via `getTokenInfoFromStorage`.
 *
 * IMPORTANT: PASETO tokens (v4.public.* and v4.local.*) cannot be decoded client-side
 * and will always return null to avoid errors.
 */
export function decodeTokenPayload(token: string): DecodedToken | null {
  try {
    if (!token || typeof token !== 'string') return null;

    // PASETO tokens cannot be decoded client-side - return null immediately
    if (token.startsWith('v4.public.') || token.startsWith('v4.local.')) {
      return getTokenInfoFromStorage(token);
    }

    const parts = token.split('.');
    if (parts.length < 3) {
      console.warn('Invalid token format: insufficient parts');
      return getTokenInfoFromStorage(token);
    }

    const purpose = parts[1] ?? '';
    const payload = parts[2];

    // If any part is missing, fall back to stored token info
    if (!payload) {
      console.warn('Invalid token format: missing payload');
      return getTokenInfoFromStorage(token);
    }

    // Only decode un-encrypted/public tokens client-side
    if (purpose !== 'public') {
      return getTokenInfoFromStorage(token);
    }

    // Convert base64url -> base64 and decode
    try {
      const json = decodeBase64UrlJson(payload);
      if (!json || typeof json !== 'object') return getTokenInfoFromStorage(token);
      return json as DecodedToken;
    } catch (err) {
      console.warn('Failed to decode token payload:', err);
      return getTokenInfoFromStorage(token);
    }
  } catch (err) {
    console.warn('Unexpected error decoding token payload:', err);
    return getTokenInfoFromStorage(token);
  }
}

function getTokenInfoFromStorage(_token: string): DecodedToken | null {
  return null; // LAUNCH GATE: no token info in browser storage
}

/**
 * Check if token is expired
 *
 * @param token - Token string
 * @param bufferMs - Buffer time in milliseconds before expiration (default: 5 minutes)
 * @returns True if token is expired or will expire within buffer time
 */
export function isTokenExpired(token: string, bufferMs: number = DEFAULT_EXPIRATION_BUFFER_MS): boolean {
  if (!token || typeof token !== 'string') return true;

  // For PASETO tokens, we cannot decode them client-side
  // Assume they are valid and let the server validate
  if (token.startsWith('v4.public.') || token.startsWith('v4.local.')) {
    return false; // Assume valid, let server validate
  }

  const payload = decodeTokenPayload(token);
  if (!payload) {
    return true; // Invalid token is considered expired
  }
  const now = Date.now();
  const expMs = (payload.exp ?? 0) * 1000; // Convert to milliseconds, default 0
  const expirationWithBuffer = expMs - bufferMs;

  return now >= expirationWithBuffer;
}

/**
 * Get seconds until token expiration
 *
 * @param token - Token string
 * @returns Seconds until expiration (negative if already expired)
 */
export function getSecondsUntilExpiration(token: string): number {
  const payload = decodeTokenPayload(token);
  if (!payload) {
    return -1; // Invalid token
  }
  const now = Math.floor(Date.now() / 1000);
  return (payload.exp ?? 0) - now;
}

/**
 * Validate token comprehensively
 *
 * @param token - Token string
 * @param bufferMs - Buffer time in milliseconds before expiration
 * @returns Comprehensive token validation result
 */
export function validateToken(token: string, bufferMs: number = DEFAULT_EXPIRATION_BUFFER_MS): TokenValidationResult {
  if (!token || typeof token !== 'string') {
    return {
      isValid: false,
      isExpired: true,
      isExpiringSoon: false,
      secondsUntilExpiration: -1,
      error: 'Token is missing or invalid'
    };
  }

  // For PASETO tokens, we cannot decode them client-side
  // Just check if token exists and has correct format, let server validate
  if (token.startsWith('v4.public.') || token.startsWith('v4.local.')) {
    return {
      isValid: true, // Assume valid, let server validate
      isExpired: false,
      isExpiringSoon: false,
      secondsUntilExpiration: 3600, // Assume 1 hour remaining
      error: undefined
    };
  }

  // For other token formats, try to decode (JWT, etc.)
  const payload = decodeTokenPayload(token);
  if (!payload) {
    return {
      isValid: false,
      isExpired: true,
      isExpiringSoon: false,
      secondsUntilExpiration: -1,
      error: 'Token format is invalid'
    };
  }

  const now = Math.floor(Date.now() / 1000);
  const secondsUntilExpiration = (payload.exp ?? 0) - now;
  const bufferSeconds = Math.floor(bufferMs / 1000);

  const isExpired = secondsUntilExpiration <= 0;
  const isExpiringSoon = secondsUntilExpiration <= bufferSeconds;

  return {
    isValid: !isExpired,
    isExpired,
    isExpiringSoon,
    secondsUntilExpiration,
    payload,
    error: isExpired ? 'Token has expired' : undefined
  };
}

/**
 * Get stored token from localStorage.
 * LAUNCH GATE: No tokens in browser; cookie-only BFF session. Always returns null.
 */
export function getStoredToken(_key: string = TOKEN_KEYS.ACCESS_TOKEN): string | null {
  return null;
}

/**
 * Store token. LAUNCH GATE: No tokens in browser; cookie-only BFF session. No-op.
 */
export function storeToken(_token: string, _key: string = TOKEN_KEYS.ACCESS_TOKEN): void {
  // No-op: do not store auth tokens in browser storage.
}

/**
 * Clear stored token. LAUNCH GATE: No tokens in browser; no-op (clearing is harmless).
 */
export function clearStoredToken(key: string = TOKEN_KEYS.ACCESS_TOKEN): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
    if (key === TOKEN_KEYS.ACCESS_TOKEN) {
      localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRY);
      localStorage.removeItem('app_token_info');
    }
  } catch {
    // ignore
  }
}

/**
 * Check if stored token is expired without decoding
 * Uses stored expiration time for performance
 *
 * @param bufferMs - Buffer time in milliseconds before expiration
 * @returns True if token is expired or will expire within buffer time
 */
export function isStoredTokenExpired(bufferMs: number = DEFAULT_EXPIRATION_BUFFER_MS): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    const expiryString = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
    if (!expiryString) {
      return true;
    }

    const expiryTime = parseInt(expiryString, 10) * 1000; // Convert to milliseconds
    const now = Date.now();
    const expirationWithBuffer = expiryTime - bufferMs;

    return now >= expirationWithBuffer;
  } catch (error) {
    console.warn('Failed to check stored token expiration:', error);
    return true;
  }
}

/**
 * Get time until stored token expires
 *
 * @returns Milliseconds until expiration (negative if expired)
 */
export function getTimeUntilStoredTokenExpiry(): number {
  if (typeof window === 'undefined') {
    return -1;
  }

  try {
    const expiryString = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
    if (!expiryString) {
      return -1;
    }

    const expiryTime = parseInt(expiryString, 10) * 1000; // Convert to milliseconds
    const now = Date.now();

    return expiryTime - now;
  } catch (error) {
    console.warn('Failed to get time until token expiry:', error);
    return -1;
  }
}

/**
 * Format time until expiration as human-readable string
 *
 * @param milliseconds - Milliseconds until expiration
 * @returns Human-readable time string
 */
export function formatTimeUntilExpiration(milliseconds: number): string {
  if (milliseconds <= 0) {
    return 'Expired';
  }

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Get stored user. LAUNCH GATE: No user in browser storage; session from BFF only. Always null.
 */
export function getStoredUser(): User | null {
  return null;
}

/** LAUNCH GATE: MFA is IdP-only; no provisional token flow. No-op. */
export function storeMfaUserId(_userId: string): void {}

/** LAUNCH GATE: MFA is IdP-only. Always null. */
export function getMfaUserId(): string | null {
  return null;
}

/** LAUNCH GATE: No-op. */
export function clearMfaUserId(): void {
  if (typeof window !== 'undefined') localStorage.removeItem(TOKEN_KEYS.MFA_USER_ID);
}

/**
 * Get active token. LAUNCH GATE: No tokens in browser; cookie-only. Always null.
 */
export function getActiveToken(): string | null {
  return null;
}
